import React, { useRef, useState, useEffect } from "react";

import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";

import SubtitleEditor from "./parts/SubtitleEditor";
import { Episode } from "../../services/episode.service";
import { VideoPlayerState } from "./types/VideoPlayerState";
import VideoPreviewPlayer, {
  VideoPreviewPlayerHandle,
} from "./parts/VideoPreviewPlayer";

interface Props {
  episode?: Episode;
}

export default function Preview({ episode }: Props) {
  const playerRef = useRef<VideoPreviewPlayerHandle>(null);
  const [videoPlayerState, setVideoPlayerState] = useState<VideoPlayerState>({
    isPlaying: false,
    currentTime: 0,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Video control methods
  const handleVideoPlay = () => {
    playerRef.current?.play();
  };

  const handleVideoPause = () => {
    playerRef.current?.pause();
  };

  // Monitor video player state changes
  useEffect(() => {}, [videoPlayerState]);

  if (isMobile) {
    // Mobile: Video first, then subtitles
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 2,
        }}
      >
        <Box sx={{ width: "100%" }}>
          {episode?.upload?.url && (
            <VideoPreviewPlayer
              ref={playerRef}
              src={episode.upload.url}
              videoPlayerState={videoPlayerState}
              onUpdatePlayerState={setVideoPlayerState}
            />
          )}
        </Box>
        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Transcript
          </Typography>
          {episode && (
            <SubtitleEditor
              episode={episode}
              currentTime={videoPlayerState?.currentTime}
              onParagraphClick={(paragraph) => {
                playerRef.current?.seekTo(paragraph.start);
                setVideoPlayerState({
                  ...videoPlayerState,
                  currentTime: paragraph.start,
                });
              }}
              onVideoPlay={handleVideoPlay}
              onVideoPause={handleVideoPause}
            />
          )}
        </Box>
      </Box>
    );
  }

  // Desktop: Subtitles left, video right
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        height: "100%", // Use 100% instead of 100vh
        overflow: "hidden", // Prevent overflow
        gap: 0,
      }}
    >
      <Box
        sx={{
          width: "30%",
          height: "100%",
          pr: 2,
          overflow: "hidden", // Prevent overflow
        }}
      >
        {episode && (
          <SubtitleEditor
            episode={episode}
            currentTime={videoPlayerState?.currentTime}
            onParagraphClick={(paragraph) => {
              playerRef.current?.seekTo(paragraph.start);
              setVideoPlayerState({
                ...videoPlayerState,
                currentTime: paragraph.start,
              });
            }}
            onVideoPlay={handleVideoPlay}
            onVideoPause={handleVideoPause}
          />
        )}
      </Box>
      <Box
        sx={{
          width: "70%",
          height: "100%",
          overflow: "hidden", // Prevent overflow
        }}
      >
        {episode?.upload?.url && (
          <VideoPreviewPlayer
            ref={playerRef}
            src={episode.upload.url}
            videoPlayerState={videoPlayerState}
            onUpdatePlayerState={setVideoPlayerState}
          />
        )}
      </Box>
    </Box>
  );
}
